import React from 'react';
import { VolatilityLadderDetails } from '../api/tradeGroups';
import './shared/MetricCards.css';

interface VolatilityAttributesSectionProps {
  volatilityLadder: VolatilityLadderDetails;
  originalQuoteSize?: number;
}

const VolatilityAttributesSection: React.FC<VolatilityAttributesSectionProps> = ({
  volatilityLadder,
  originalQuoteSize
}) => {
  // Formatting utilities
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(decimals)}%`;
  };

  const formatRelativeTime = (timestamp: number | null | undefined): string => {
    if (!timestamp) return 'Never';
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getRegimeBadgeClass = (regime: string | null): string => {
    if (!regime) return '';
    switch (regime.toUpperCase()) {
      case 'LOW': return 'regime-low';
      case 'NORMAL': return 'regime-normal';
      case 'HIGH': return 'regime-high';
      case 'EXTREME': return 'regime-extreme';
      default: return '';
    }
  };

  const getAdaptiveModeBadge = (adaptiveMode: boolean): React.JSX.Element => {
    return (
      <span className={`adaptive-badge ${adaptiveMode ? 'adaptive-active' : 'adaptive-static'}`}>
        {adaptiveMode ? 'Adaptive' : 'Static'}
      </span>
    );
  };

  const getPriceBounds = (): string => {
    const { lowerBoundPrice, upperBoundPrice } = volatilityLadder;
    if (!lowerBoundPrice && !upperBoundPrice) return 'Not Configured';
    if (lowerBoundPrice && upperBoundPrice) {
      return `${formatCurrency(lowerBoundPrice)} - ${formatCurrency(upperBoundPrice)}`;
    }
    if (lowerBoundPrice) return `Min: ${formatCurrency(lowerBoundPrice)}`;
    if (upperBoundPrice) return `Max: ${formatCurrency(upperBoundPrice)}`;
    return 'Not Configured';
  };

  // Check if volatility check is overdue
  const isVolatilityCheckOverdue = (): boolean => {
    const { lastVolatilityCheck, volatilityCheckIntervalHours } = volatilityLadder;
    if (!lastVolatilityCheck || !volatilityCheckIntervalHours) return false;
    const now = Date.now();
    const diff = now - lastVolatilityCheck;
    const hours = diff / (1000 * 60 * 60);
    return hours > volatilityCheckIntervalHours * 1.5; // 50% buffer
  };

  return (
    <div className="volatility-attributes-section">
      {/* Configuration Section */}
      <div className="metrics-grid" style={{ marginBottom: '2rem' }}>
        <h3 style={{ 
          gridColumn: '1 / -1', 
          color: '#94a3b8', 
          fontSize: '0.875rem', 
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '1rem',
          borderBottom: '1px solid #334155',
          paddingBottom: '0.5rem'
        }}>
          Volatility Configuration
        </h3>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Target Order Amount</div>
            <div className="metric-value">{formatCurrency(volatilityLadder.targetOrderAmount)}</div>
          </div>
          <div className="metric-footer">Per order</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Ladder Rungs</div>
            <div className="metric-value">{volatilityLadder.numberOfRungs ?? 'N/A'}</div>
          </div>
          <div className="metric-footer">Initial</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Ladder Spacing</div>
            <div className="metric-value">
              {volatilityLadder.ladderSpacingPct !== null 
                ? `${formatPercent(volatilityLadder.ladderSpacingPct)} (${formatCurrency(volatilityLadder.ladderSpacingAmount)})`
                : 'N/A'}
            </div>
          </div>
          <div className="metric-footer">Spacing</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Volatility Period</div>
            <div className="metric-value">{volatilityLadder.volatilityPeriod || 'N/A'}</div>
          </div>
          <div className="metric-footer">Analysis</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Initial Deployment</div>
            <div className="metric-value">{formatPercent(volatilityLadder.initialDeploymentPct)}</div>
          </div>
          <div className="metric-footer">Of capital</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Profit Target</div>
            <div className="metric-value">{formatPercent(volatilityLadder.profitTargetPct)}</div>
          </div>
          <div className="metric-footer">Target</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Adaptive Mode</div>
            <div className="metric-value">{getAdaptiveModeBadge(volatilityLadder.adaptiveMode)}</div>
          </div>
          <div className="metric-footer">Status</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Price Bounds</div>
            <div className="metric-value" style={{ fontSize: '1rem' }}>{getPriceBounds()}</div>
          </div>
          <div className="metric-footer">Range limits</div>
        </div>
      </div>

      {/* Current State Section */}
      <div className="metrics-grid">
        <h3 style={{ 
          gridColumn: '1 / -1', 
          color: '#94a3b8', 
          fontSize: '0.875rem', 
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '1rem',
          borderBottom: '1px solid #334155',
          paddingBottom: '0.5rem'
        }}>
          Current Volatility State
        </h3>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Volatility Regime</div>
            <div className="metric-value">
              {volatilityLadder.currentVolatilityRegime ? (
                <span className={`regime-badge ${getRegimeBadgeClass(volatilityLadder.currentVolatilityRegime)}`}>
                  {volatilityLadder.currentVolatilityRegime}
                </span>
              ) : 'N/A'}
            </div>
          </div>
          <div className="metric-footer">Current</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Volatility Percentile</div>
            <div className="metric-value">{formatPercent(volatilityLadder.volatilityPercentile)}</div>
          </div>
          <div className="metric-footer">Historical</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Current ATR</div>
            <div className="metric-value">{formatCurrency(volatilityLadder.currentAtr)}</div>
          </div>
          <div className="metric-footer">True Range</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Average ATR</div>
            <div className="metric-value">{formatCurrency(volatilityLadder.averageAtr)}</div>
          </div>
          <div className="metric-footer">Period Avg</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Capital Deployed</div>
            <div className="metric-value">{formatPercent(volatilityLadder.capitalDeployedPct)}</div>
            {originalQuoteSize && volatilityLadder.capitalDeployedPct && (
              <div className="metric-subtitle">
                {formatCurrency(originalQuoteSize * volatilityLadder.capitalDeployedPct)} of {formatCurrency(originalQuoteSize)}
              </div>
            )}
          </div>
          <div className="metric-footer">Percentage</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Last Check</div>
            <div className="metric-value" style={{ color: isVolatilityCheckOverdue() ? '#ef4444' : '#f1f5f9' }}>
              {formatRelativeTime(volatilityLadder.lastVolatilityCheck)}
              {isVolatilityCheckOverdue() && <span style={{ marginLeft: '0.5rem' }}>⚠️</span>}
            </div>
          </div>
          <div className="metric-footer">Volatility</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Last Rebalance</div>
            <div className="metric-value">{formatRelativeTime(volatilityLadder.lastRebalanceTime)}</div>
          </div>
          <div className="metric-footer">Adjustment</div>
        </div>

        <div className="metric-card">
          <div className="metric-content">
            <div className="metric-label">Last Dip Buying</div>
            <div className="metric-value">{formatRelativeTime(volatilityLadder.lastDipBuyingTime)}</div>
          </div>
          <div className="metric-footer">Hot money</div>
        </div>
      </div>

      <style>{`
        .volatility-attributes-section {
          margin-bottom: 2rem;
        }

        .regime-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .regime-low {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid #10b981;
        }

        .regime-normal {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
          border: 1px solid #3b82f6;
        }

        .regime-high {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
          border: 1px solid #f59e0b;
        }

        .regime-extreme {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid #ef4444;
        }

        .adaptive-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .adaptive-active {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
          border: 1px solid #3b82f6;
        }

        .adaptive-static {
          background: rgba(107, 114, 128, 0.2);
          color: #6b7280;
          border: 1px solid #6b7280;
        }
      `}</style>
    </div>
  );
};

export default VolatilityAttributesSection;
